import type { ReactNode } from "react";
import Badge from "../Badge/Badge";
import Icon from "../Icon/Icon";

export interface StepListItem {
  id: string;
  title: ReactNode;
  /** small pill next to the title, for example "Recommended" */
  badge?: ReactNode;
  /** "valid" shows the badge in lime */
  badgeTone?: "default" | "valid";
  /** smaller steps shown under the active step */
  substeps?: { id: string; title: ReactNode }[];
  activeSubstep?: string;
}

export interface StepListProps {
  steps: StepListItem[];
  /** index of the current step; earlier steps show a check */
  activeIndex: number;
  /** text under finished steps, for example "Completed" */
  completedLabel?: ReactNode;
}

/**
 * Numbered steps for the dark progress rail (see ProgressRail).
 * Styles: src/pages/onboarding/onboardingProgress.css (.onboarding-progress__*).
 */
const StepList = ({ steps, activeIndex, completedLabel }: StepListProps) => (
  <ol className="onboarding-progress__steps">
    {steps.map((step, index) => {
      const isActive = index === activeIndex;
      const isComplete = index < activeIndex;
      const activeSubstepIndex =
        step.substeps?.findIndex(({ id }) => id === step.activeSubstep) ?? -1;

      return (
        <li
          key={step.id}
          className={`onboarding-progress__step ${
            isActive ? "is-active" : isComplete ? "is-complete" : ""
          }`}
          aria-current={isActive ? "step" : undefined}
        >
          <span className="onboarding-progress__marker">
            {isComplete ? <Icon name="check" size={16} /> : index + 1}
          </span>
          <div className="onboarding-progress__step-content">
            <div className="onboarding-progress__title-row">
              <span className="onboarding-progress__title text-capitalize">
                {step.title}
              </span>
              {step.badge && (
                <Badge tone={step.badgeTone === "valid" ? "accent" : "onDark"}>
                  {step.badge}
                </Badge>
              )}
            </div>

            {isComplete && completedLabel && (
              <span className="onboarding-progress__completed">
                {completedLabel}
              </span>
            )}

            {isActive && step.substeps && (
              <ol className="onboarding-progress__substeps">
                {step.substeps.map((substep, substepIndex) => {
                  const isSubActive = substep.id === step.activeSubstep;
                  const isSubComplete = activeSubstepIndex > substepIndex;
                  return (
                    <li
                      key={substep.id}
                      className={`onboarding-progress__substep ${
                        isSubActive
                          ? "is-active"
                          : isSubComplete
                            ? "is-complete"
                            : ""
                      }`}
                      aria-current={isSubActive ? "step" : undefined}
                    >
                      <span className="onboarding-progress__submarker">
                        {isSubComplete && <Icon name="check" size={12} />}
                      </span>
                      <span>{substep.title}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);

export default StepList;
